import { Quill } from 'vue-quill-editor';

const Delta = Quill.import('delta');

// Soft line break (<br>) support. Quill's data model is line/block based, so a
// plain Enter starts a new paragraph (<p>). Register a Break embed blot + a
// Shift+Enter binding so a modifier+Return inserts a <br> within the current
// paragraph — matching mail-client behaviour and the actual rendered markup.
//
// The blot extends Parchment's RAW Embed (a length-1 leaf) — NOT Quill's guarded
// `blots/embed`. The guarded embed wraps its node in U+FEFF caret guards designed for
// inline <span> embeds; a void <br> cannot hold those guards, and that mismatch corrupted
// caret index→DOM mapping (Firefox landed the caret on the wrong line and a second
// Shift+Enter jumped it back up). A raw leaf embed maps cleanly in every browser.
// We also avoid Quill's stock Break blot, which self-removes the moment it gains a
// sibling. Proven pattern: https://billauer.se/blog/2021/12/quill-br-shift-enter/
const SoftBreakEmbed = Quill.import('parchment').Embed;

class SoftBreak extends SoftBreakEmbed {}
SoftBreak.blotName = 'softbreak';
SoftBreak.tagName = 'BR';
SoftBreak.className = 'ql-softbreak';
Quill.register(SoftBreak, true);

// Multi-line blockquote. Quill's stock `blockquote` is a single-line Block, so a
// quote spanning several paragraphs becomes several separate <blockquote> elements
// with margins between them — a broken left bar. Following Quill's own List/ListItem
// container pattern, we model the blockquote as a CONTAINER (<blockquote>) holding
// QuoteLine paragraphs (<p class="ql-quote">). Consecutive quote lines merge into one
// container, so a forwarded message renders as one continuous quote with real
// paragraph spacing — in the editor AND the sent email.
const Parchment = Quill.import('parchment');
const Container = Quill.import('blots/container');
const BlockBlot = Quill.import('blots/block');

class QuoteLine extends BlockBlot {
    static formats(domNode) {
        return domNode.tagName === this.tagName ? undefined : super.formats(domNode);
    }

    format(name, value) {
        if (name === QuoteContainer.blotName && !value) {
            this.replaceWith(Parchment.create(this.statics.scope));
        } else {
            super.format(name, value);
        }
    }

    remove() {
        if (this.prev == null && this.next == null) {
            this.parent.remove();
        } else {
            super.remove();
        }
    }

    replaceWith(name, value) {
        this.parent.isolate(this.offset(this.parent), this.length());
        if (name === this.parent.statics.blotName) {
            this.parent.replaceWith(name, value);
            return this;
        } else {
            this.parent.unwrap();
            return super.replaceWith(name, value);
        }
    }
}

QuoteLine.blotName = 'blockquote-line';
QuoteLine.tagName = 'P';
QuoteLine.className = 'ql-quote';

class QuoteContainer extends Container {
    static formats(domNode) {
        return domNode.tagName === this.tagName ? true : undefined;
    }

    format(name, value) {
        if (this.children.length > 0) {
            this.children.tail.format(name, value);
        }
    }

    formats() {
        return { [this.statics.blotName]: this.statics.formats(this.domNode) };
    }

    insertBefore(blot, ref) {
        if (blot instanceof QuoteLine) {
            super.insertBefore(blot, ref);
        } else {
            const index = ref == null ? this.length() : ref.offset(this);
            const after = this.split(index);
            after.parent.insertBefore(blot, after);
        }
    }

    optimize(context) {
        super.optimize(context);
        const next = this.next;
        if (next != null && next.prev === this &&
            next.statics.blotName === this.statics.blotName &&
            next.domNode.tagName === this.domNode.tagName) {
            next.moveChildren(this);
            next.remove();
        }
    }

    replace(target) {
        if (target.statics.blotName !== this.statics.blotName) {
            const replacement = Parchment.create(this.statics.defaultChild);
            target.moveChildren(replacement);
            this.appendChild(replacement);
        }
        super.replace(target);
    }
}

QuoteContainer.blotName = 'blockquote';
QuoteContainer.tagName = 'BLOCKQUOTE';
QuoteContainer.scope = Parchment.Scope.BLOCK_BLOT;
QuoteContainer.defaultChild = 'blockquote-line';
QuoteContainer.allowedChildren = [QuoteLine];

Quill.register(QuoteContainer, true);
Quill.register(QuoteLine, true);

// Insert a soft line break (<br>) at the current selection.
//
// When the break lands at the very END of its block, a second "filler" <br> is appended.
// This is required, not decorative: a block that ends in a single <br> has no editable
// position after it, so Firefox refuses to move the caret onto the new line (it stays
// after the last character). Browsers use the same bogus-<br> trick internally. The caret
// is placed after the FIRST break either way; Chrome consumes the filler as you type.
// Mid-line breaks (content follows) need no filler, so we only add it when the break has
// no next sibling in its block. Proven pattern (billauer.se/blog/2021/12/quill-br-shift-enter).
const insertLineBreak = (quill) => {
    const range = quill.getSelection(true);
    if (!range) {
        return;
    }
    if (range.length > 0) {
        quill.deleteText(range.index, range.length, Quill.sources.USER);
    }

    quill.insertEmbed(range.index, 'softbreak', true, Quill.sources.USER);

    // getLeaf(index + 1) resolves to the break we just inserted (a boundary resolves to the
    // preceding leaf). Its `next` sibling tells us whether real content follows: if there is
    // none, the break ends the block, the new line is empty, and Firefox has no caret
    // position there — so append a filler break. A mid-line break (e.g. "A|B", where the
    // break's next is the "B" text) already has a caret home and gets no filler. A single
    // filler total is kept: a second Shift+Enter's break sees the existing filler as its
    // next sibling, so it does not stack another.
    const insertedBreak = quill.getLeaf(range.index + 1)[0];
    if (!insertedBreak || !insertedBreak.next) {
        quill.insertEmbed(range.index + 1, 'softbreak', true, Quill.sources.USER);
    }

    quill.setSelection(range.index + 1, Quill.sources.SILENT);
};

// A keyboard *binding* for Shift+Return is unreliable: Quill's own keydown listener
// can bail before bindings run, and other ENTER bindings compete. Intercepting on
// the capture phase (before Quill's bubble-phase listener on the same root) and
// stopping propagation guarantees a soft break, leaving plain Return as new-paragraph.
const Module = Quill.import('core/module');

class SmartBreakKeyboard extends Module {
    constructor(quill, options) {
        super(quill, options);
        quill.root.addEventListener('keydown', (event) => {
            if (event.defaultPrevented) {
                return;
            }
            if ((event.which || event.keyCode) !== 13 || !event.shiftKey) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            insertLineBreak(quill);
        }, true);
    }
}

Quill.register('modules/smartBreak', SmartBreakKeyboard);

const bindings = {
    // NOTE
    // disable default (ordered) list generation
    // on space with . prefix
    'list autofill': {
        key: ' ',
        collapsed: true,
        format: { list: false },
        prefix: /^\s*?(1\.|-|\*|\[ ?\]|\[x\])$/
        // handler: function(range, context) {
        //
        //     let length = context.prefix.length;
        //     let [line, offset] = this.quill.getLine(range.index);
        //     if (offset > length) return true;
        //
        //     let value;
        //     switch (context.prefix.trim()) {
        //         case '[]':
        //         case '[ ]':
        //             value = 'unchecked';
        //             break;
        //         case '[x]':
        //             value = 'checked';
        //             break;
        //         case '-':
        //         case '*':
        //             value = 'bullet';
        //             break;
        //         default:
        //             value = 'ordered';
        //     }
        //
        //     if (value) {
        //         this.quill.insertText(range.index, ' ', Quill.sources.USER);
        //         this.quill.history.cutoff();
        //
        //         let delta = new Delta()
        //             .retain(range.index - offset)
        //             .delete(length + 1)
        //             .retain(line.length() - 2 - offset)
        //             .retain(1, { list: value });
        //
        //         this.quill.updateContents(delta, Quill.sources.USER);
        //         this.quill.history.cutoff();
        //         this.quill.setSelection(range.index - length, Quill.sources.SILENT);
        //     }
        // }
    }
};

const config = {
    //theme: 'bubble',
    theme: 'snow',
    placeholder: '',
    bounds: '#app',
    modules: {
        // Prevent Quill from synthesizing empty paragraphs between blocks
        // when HTML is loaded back into the editor.
        clipboard: {
            matchVisual: false,
            // Paste-only (DOMPurify hook, not a Quill matcher, so reloading saved
            // HTML is untouched): drop spacer paragraphs. Outlook writes a blank
            // line as <p>&nbsp;</p>, Apple Mail as <div><br></div> (substituted to
            // <p><br></p> by quill-paste-smart in this same pass). Our editor
            // paragraphs already carry a margin (_quill.sass), so each spacer
            // rendered as two-plus blank lines instead of one.
            hooks: {
                uponSanitizeElement: node => {
                    if (node.tagName !== 'P' || !node.parentNode) return;
                    if (node.textContent.trim() === '' && !node.querySelector('img, iframe, video')) {
                        node.parentNode.removeChild(node);
                    }
                }
            },
            matchers: [
                // Preserve soft line breaks: a <br> that sits BETWEEN content is a
                // real soft break (load it as the 'softbreak' embed instead of letting
                // Quill split it into two paragraphs on paste / HTML load). Quill's own
                // empty-line <br> (the sole child of a block) is left to the block,
                // so empty paragraphs aren't doubled.
                ['BR', (node, delta) => {
                    if (!node.previousSibling && !node.nextSibling) {
                        return delta;
                    }
                    // Return a FRESH delta with just the softbreak embed — Quill's
                    // default matchBreak runs first and inserts '\n' (which splits
                    // paragraphs); replacing the delta drops that '\n' so the <br>
                    // stays inline.
                    return new Delta().insert({ softbreak: true });
                }]
            ]
        },
        imageBlocker: true,
        smartBreak: true,
        keyboard: { bindings },
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'header': '1' }, { 'header': '2' }, { 'header': '3' }, { 'header': '4' }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
                ['link'],
                ['placeholder'],
                ['text-generation'],
                ['clean']
            ]
        },
        // AI text generation; the button hides itself unless the CMS booked ai-content.
        textGeneration: {},
        link: {
            types: ['internal', 'external', 'email', 'phone', 'media'],
            pages: [],
            submit: [
                { label: 'Textlink' },
                { class: 'el-button el-button--info el-button--mini', label: 'Kleiner Button' },
                { class: 'el-button el-button--info el-button--medium', label: 'Mittlerer Button' },
                { class: 'el-button el-button--info el-button--large', label: 'Großer Button' }
            ],
            content: {
                external: 'Externer Link',
                email: 'E-Mail',
                phone: 'Telefonnummer',
                openInNewTab: 'Link in einem neuen Fenster/Tab öffnen'
            }
        },
        placeholder: {
            variables: [],
            placeholder: 'Variable suchen...'
        }
    }
    // formats: []
};

export default config;
